import { toggleMark } from 'prosemirror-commands'
import { Mark, MarkType, Node } from 'prosemirror-model'
import { Selection, Transaction } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { useSyncExternalStore } from 'react'
import { textboxSchema } from './textboxSchema'

// Active textBox editor for the right-rail format controls. Cleared on unmount, not blur (toolbar clicks blur but the PM selection survives).
let activeView: EditorView | null = null

const listeners = new Set<() => void>()

// How much of the current selection carries a mark: the whole selection ('all'), part of it
// ('some' = mixed), or none. Empty selections collapse to 'all'/'none' (stored marks, no mixing).
export type MarkPresence = 'all' | 'some' | 'none'

// The selection's per-run font weight: a number when every run resolves to the same weight, 'mixed' when
// they differ, or null when no run sets a weight explicitly (the toolbar then shows the whole-shape weight).
// Bold is just weight >= 700 — the single font_weight channel, so there is no separate `strong` presence.
export type ProseWeight = number | 'mixed' | null

export interface ActiveProseMarks {
  weight: ProseWeight
  em: MarkPresence
  underline: MarkPresence
  strike: MarkPresence
  color: string | null
  hasMarks: boolean
}

let snapshot: ActiveProseMarks | null = null

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = () => snapshot

const getServerSnapshot = () => null

const nodeHasInlineMark = (node: Node): boolean => node.isText && node.marks.length > 0

const docHasInlineMarks = (doc: Node): boolean => {
  let found = false
  doc.descendants((node) => {
    if (found) return false
    if (nodeHasInlineMark(node)) found = true
    return !found
  })
  return found
}

const removeAllMarks = (tr: Transaction, doc: Node): Transaction => {
  Object.values(textboxSchema.marks).forEach((markType) => tr.removeMark(0, doc.content.size, markType))
  return tr
}

// Tri-state presence of a mark across the selection: 'all' when every text node carries it, 'some'
// when only part does (a mixed selection), 'none' otherwise. An empty selection reads the stored/
// cursor marks and can only be 'all' or 'none'. rangeHasMark would answer "any" — it can't tell a
// fully-marked selection from a partial one, which is exactly the distinction the toolbar needs.
const markPresence = (view: EditorView, type?: MarkType): MarkPresence => {
  if (!type) return 'none'
  const { from, $from, to, empty } = view.state.selection
  if (empty) return type.isInSet(view.state.storedMarks || $from.marks()) ? 'all' : 'none'

  let sawMarked = false
  let sawUnmarked = false
  view.state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return true
    if (type.isInSet(node.marks)) sawMarked = true
    else sawUnmarked = true
    return true
  })
  if (sawMarked && sawUnmarked) return 'some'
  if (sawMarked) return 'all'
  return 'none'
}

const activeColor = (view: EditorView): string | null => {
  const type = textboxSchema.marks.colored
  if (!type) return null
  const { $from } = view.state.selection
  const mark = type.isInSet(view.state.storedMarks || $from.marks())
  return (mark?.attrs?.color as string | undefined) ?? null
}

// A run's effective weight: an explicit font_weight mark, else a legacy strong mark counts as 700 (bold),
// else undefined — the run inherits the whole-shape fontWeight, exactly as both renderers resolve it.
const runWeight = (marks: readonly Mark[]): number | undefined => {
  const set = [...marks]
  const fontWeight: MarkType = textboxSchema.marks.font_weight
  const strong: MarkType = textboxSchema.marks.strong
  const fw = fontWeight.isInSet(set)
  if (fw) return fw.attrs.weight as number
  if (strong.isInSet(set)) return 700
  return undefined
}

// The weight across the selection: a number when uniform, 'mixed' when runs differ (or some inherit and
// some don't), null when nothing sets a weight (an empty selection reads the stored/cursor marks).
const weightAcrossSelection = (view: EditorView): ProseWeight => {
  const { from, $from, to, empty } = view.state.selection
  if (empty) return runWeight(view.state.storedMarks || $from.marks()) ?? null

  const seen = new Set<number | undefined>()
  view.state.doc.nodesBetween(from, to, (node) => {
    if (node.isText) seen.add(runWeight(node.marks))
    return true
  })
  if (seen.size === 0) return null
  if (seen.size > 1) return 'mixed'
  return [...seen][0] ?? null
}

const recompute = () => {
  snapshot = activeView
    ? {
        weight: weightAcrossSelection(activeView),
        em: markPresence(activeView, textboxSchema.marks.em),
        underline: markPresence(activeView, textboxSchema.marks.underline),
        strike: markPresence(activeView, textboxSchema.marks.strike),
        color: activeColor(activeView),
        hasMarks: docHasInlineMarks(activeView.state.doc),
      }
    : null
  listeners.forEach((listener) => listener())
}

export const setActiveProseView = (view: EditorView) => {
  activeView = view
  recompute()
}

export const clearActiveProseView = (view: EditorView) => {
  if (activeView === view) {
    activeView = null
    recompute()
  }
}

export const getActiveProseView = (): EditorView | null => activeView

export const notifyProseSelectionChanged = () => recompute()

export const useActiveProseMarks = (): ActiveProseMarks | null =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

export type ProseMarkName = 'em' | 'underline' | 'strike'

// Returns false when no textBox editor is active, so callers fall back to whole-shape styling.
export const toggleProseMark = (name: ProseMarkName): boolean => {
  const markType = textboxSchema.marks[name]
  if (!activeView || !markType) return false

  toggleMark(markType)(activeView.state, activeView.dispatch)
  activeView.focus()
  return true
}

// Deterministically add or remove a mark across the selection (unlike toggleProseMark, which flips —
// and would add-to-all on a mixed selection, so it can't reliably turn one OFF). Used by controls that
// set an explicit target state, e.g. picking Bold/Regular from the weight dropdown while editing text.
// On an empty selection it sets/clears the STORED mark (styling the next-typed text) exactly like
// toggleProseMark, so the weight dropdown's Bold/Regular match the Bold button at a bare cursor instead
// of falling through to a whole-shape weight write. Returns false only when no textBox is being edited.
export const setProseMark = (name: ProseMarkName, active: boolean): boolean => {
  const markType = textboxSchema.marks[name]
  if (!activeView || !markType) return false

  const { from, to, empty } = activeView.state.selection
  let tr
  if (empty) {
    tr = active ? activeView.state.tr.addStoredMark(markType.create()) : activeView.state.tr.removeStoredMark(markType)
  } else {
    tr = active
      ? activeView.state.tr.addMark(from, to, markType.create())
      : activeView.state.tr.removeMark(from, to, markType)
  }
  activeView.dispatch(tr)
  activeView.focus()
  return true
}

// On an empty selection this stores the colour on the next-typed text (like toggleProseMark) rather
// than returning false and letting the caller write an invisible whole-shape colour the run mask hides.
// Returns false only when no textBox is being edited.
export const applyProseColor = (color: string): boolean => {
  const type = textboxSchema.marks.colored
  if (!activeView || !type) return false

  const { from, to, empty } = activeView.state.selection
  const tr = empty
    ? activeView.state.tr.addStoredMark(type.create({ color }))
    : activeView.state.tr.removeMark(from, to, type).addMark(from, to, type.create({ color }))
  activeView.dispatch(tr)
  activeView.focus()
  return true
}

// Set an explicit per-run font weight on a transaction — over the selected range, or the stored/next-typed
// marks when the selection is empty — clearing any legacy `strong` mark so font_weight is the single bold
// channel. Shared by setProseWeight and the editor's Mod-b keymap so the button, the dropdown and the
// keyboard all write the same representation.
export const applyWeightToTransaction = (tr: Transaction, selection: Selection, weight: number): Transaction => {
  const fontWeight = textboxSchema.marks.font_weight
  const strong = textboxSchema.marks.strong
  const { from, to, empty } = selection
  if (empty) {
    const cleared = strong ? tr.removeStoredMark(strong) : tr
    return cleared.removeStoredMark(fontWeight).addStoredMark(fontWeight.create({ weight }))
  }
  const cleared = strong ? tr.removeMark(from, to, strong) : tr
  return cleared.removeMark(from, to, fontWeight).addMark(from, to, fontWeight.create({ weight }))
}

// Apply a per-run weight (incl. bold = 700) to the active editor's selection, so the weight dropdown and
// Bold button style only the selected text — or the next-typed text at a bare cursor. Returns false only
// when no textBox is being edited, so callers fall back to the whole-shape weight.
export const setProseWeight = (weight: number): boolean => {
  if (!activeView || !textboxSchema.marks.font_weight) return false

  activeView.dispatch(applyWeightToTransaction(activeView.state.tr, activeView.state.selection, weight))
  activeView.focus()
  return true
}

// Live-editor clear. Returns false when there's no active editor or nothing to clear (callers fall back to stripRichTextMarks).
export const clearProseMarks = (): boolean => {
  if (!activeView) return false

  const { doc, tr } = activeView.state
  removeAllMarks(tr, doc)
  if (!tr.steps.length) return false

  activeView.dispatch(tr)
  activeView.focus()
  return true
}

// Shape-level (no live editor) helpers reading the persisted richText doc JSON, so the toolbar reflects +
// clears marks while a textBox is merely SELECTED. These walk the RAW JSON rather than parse+normalize:
// parsing would flatten a legacy prose-core doc (headings/tables/lists) into the scoped schema, so using it
// to clear marks would destroy block structure. The raw walk touches only inline `marks`.

const jsonHasMarks = (node: unknown): boolean => {
  if (!node || typeof node !== 'object') return false
  const { marks, content } = node as { marks?: unknown[]; content?: unknown[] }
  if (Array.isArray(marks) && marks.length > 0) return true
  return Array.isArray(content) && content.some(jsonHasMarks)
}

export const richTextHasMarks = (richText?: Record<string, unknown> | null): boolean => jsonHasMarks(richText)

// Recursively drop inline `marks`, preserving every block, attr and text run exactly — non-destructive.
const stripMarksFromJson = (node: unknown): unknown => {
  if (!node || typeof node !== 'object') return node
  const { marks: _marks, content, ...rest } = node as { marks?: unknown; content?: unknown[] }
  const stripped: Record<string, unknown> = { ...rest }
  if (Array.isArray(content)) stripped.content = content.map(stripMarksFromJson)
  return stripped
}

export const stripRichTextMarks = (richText: Record<string, unknown>): Record<string, unknown> =>
  stripMarksFromJson(richText) as Record<string, unknown>
